/**
 * Gamification Store
 *
 * Manages XP, badges, levels, streaks, and leaderboard state. Tracks 15 levels,
 * 7 badge categories, weekly streaks, and provides leaderboard rankings by XP,
 * challenges completed, or streak length. Triggers badge-earned notifications
 * when milestones are reached.
 *
 * @module useGamificationStore
 *
 * @example
 * ```tsx
 * import { useGamificationStore } from '@/store/useGamificationStore'
 *
 * function XpBar() {
 *   const { totalXp, currentLevel, xpProgress, fetchStats } = useGamificationStore()
 *
 *   useEffect(() => { fetchStats(userId) }, [userId])
 *
 *   return (
 *     <ProgressBar
 *       label={`Level ${currentLevel.level}: ${currentLevel.name}`}
 *       value={xpProgress.progress}
 *       max={xpProgress.toNext}
 *     />
 *   )
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  Badge,
  UserBadge,
  XPEvent,
  GamificationStats,
  LeaderboardEntry,
  LeaderboardType,
  LevelDefinition,
  BadgeProgress,
} from '../types'
import { calculateLevel, calculateXpProgress } from '../types'
import * as gamificationApi from '../api/gamification.api'

interface GamificationState {
  // Badges
  allBadges: Badge[]
  userBadges: UserBadge[]

  // XP & Level
  totalXp: number
  currentLevel: LevelDefinition
  xpProgress: { progress: number; toNext: number }
  recentXpEvents: XPEvent[]

  // Stats
  stats: GamificationStats | null
  currentStreak: number

  // Leaderboard
  leaderboard: LeaderboardEntry[]
  leaderboardType: LeaderboardType

  // Category counts (for Journey Map)
  categoryCounts: Record<string, number>

  // Badge progress
  badgeProgress: BadgeProgress[]

  // Anti-gaming
  dailyXpRemaining: number

  // UI State
  isLoading: boolean
  error: string | null
  newBadgeEarned: UserBadge | null

  // Actions
  fetchAllBadges: () => Promise<void>
  fetchUserBadges: (userId: string) => Promise<void>
  fetchStats: (userId: string) => Promise<void>
  fetchLeaderboard: (type?: LeaderboardType) => Promise<void>
  checkForNewBadges: (userId: string) => Promise<UserBadge[]>
  clearNewBadge: () => void
  refreshXp: (userId: string) => Promise<void>
  fetchCategoryCounts: (userId: string) => Promise<void>
  fetchBadgeProgress: (userId: string) => Promise<void>
}

export const useGamificationStore = createWithEqualityFn<GamificationState>((set, get) => ({
  // Initial state
  allBadges: [],
  userBadges: [],
  totalXp: 0,
  currentLevel: { level: 1, name: 'Newcomer', minXp: 0, maxXp: 100, color: 'gray' },
  xpProgress: { progress: 0, toNext: 100 },
  recentXpEvents: [],
  stats: null,
  currentStreak: 0,
  categoryCounts: {},
  badgeProgress: [],
  leaderboard: [],
  leaderboardType: 'xp',
  dailyXpRemaining: 500,
  isLoading: false,
  error: null,
  newBadgeEarned: null,

  // Fetch all available badges
  fetchAllBadges: async () => {
    set({ isLoading: true, error: null })
    try {
      const badges = await gamificationApi.getAllBadges()
      set({ allBadges: badges, isLoading: false })
    } catch (error) {
      set({ error: 'Failed to load badges', isLoading: false })
    }
  },

  // Fetch user's earned badges
  fetchUserBadges: async (userId: string) => {
    set({ isLoading: true, error: null })
    try {
      const badges = await gamificationApi.getUserBadges(userId)
      set({ userBadges: badges, isLoading: false })
    } catch (error) {
      set({ error: 'Failed to load user badges', isLoading: false })
    }
  },

  // Fetch gamification stats
  fetchStats: async (userId: string) => {
    set({ isLoading: true, error: null })
    try {
      const stats = await gamificationApi.getGamificationStats(userId)
      const level = calculateLevel(stats.totalXp)
      const progress = calculateXpProgress(stats.totalXp)

      set({
        stats,
        totalXp: stats.totalXp,
        currentLevel: level,
        xpProgress: progress,
        currentStreak: stats.currentStreak,
        recentXpEvents: stats.recentXpEvents,
        isLoading: false,
      })
    } catch (error) {
      set({ error: 'Failed to load stats', isLoading: false })
    }
  },

  // Fetch leaderboard
  fetchLeaderboard: async (type: LeaderboardType = 'xp') => {
    set({ isLoading: true, error: null, leaderboardType: type })
    try {
      const leaderboard = await gamificationApi.getLeaderboard(type, 10)
      set({ leaderboard, isLoading: false })
    } catch (error) {
      set({ error: 'Failed to load leaderboard', isLoading: false })
    }
  },

  // Check for new badges
  checkForNewBadges: async (userId: string) => {
    try {
      const newBadges = await gamificationApi.checkAndAwardBadges(userId)
      if (newBadges.length > 0) {
        // Show the first new badge earned
        set({ newBadgeEarned: newBadges[0] })
        // Refresh user badges
        await get().fetchUserBadges(userId)
      }
      return newBadges
    } catch (error) {
      console.error('Failed to check for badges:', error)
      return []
    }
  },

  // Clear the new badge notification
  clearNewBadge: () => {
    set({ newBadgeEarned: null })
  },

  // Refresh XP total
  refreshXp: async (userId: string) => {
    try {
      const totalXp = await gamificationApi.getUserTotalXP(userId)
      const level = calculateLevel(totalXp)
      const progress = calculateXpProgress(totalXp)

      set({
        totalXp,
        currentLevel: level,
        xpProgress: progress,
      })
    } catch (error) {
      console.error('Failed to refresh XP:', error)
    }
  },

  // Fetch category counts for Journey Map
  fetchCategoryCounts: async (userId: string) => {
    set({ isLoading: true, error: null })
    try {
      const counts = await gamificationApi.getCategoryCounts(userId)
      set({ categoryCounts: counts, isLoading: false })
    } catch (error) {
      set({ error: 'Failed to load category counts', isLoading: false })
    }
  },

  // Fetch progress toward unearned badges
  fetchBadgeProgress: async (userId: string) => {
    try {
      const progress = await gamificationApi.getBadgeProgress(userId)
      set({ badgeProgress: progress })
    } catch (error) {
      console.error('Failed to load badge progress:', error)
    }
  },
}))
